/**
 * Advisors component module.
 * @module investor/pages/Advisors
 *
 */
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import {_openModal} from '../../shared/store';
import {
  Column, Columnizer,
  DescriptiveText,
  Icon,
  LoadingSpinner,
  Page,
  Row,
  TextInput,
  Tile,
} from '../../shared';
import {formatter} from '../../shared/utils/utils';
import StickyHeaderBar from '../shared/components/StickyHeaderBar';

/**
 * Advisors component
 * @name Advisors
 */
const Advisors = ({openModal, session}) => {
  const marginWidth = 20;
  const contactInfoColumnMinWidth = 270;
  const accountsManagingColumnMinWidth = 300;
  const [allAdvisors, setAllAdvisors] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);

  const {data: advisors, error: advisorsError, mutate: refreshAdvisors} = useSWR('/user/advisors?accounts=1');
  const {data: institutions, error: institutionsError, mutate: refreshInstitutions} = useSWR('/user/institutions?accounts=1');

  const openManageAdvisorAccountsModal = (advisor) => {
    const preparedAccounts = allAccounts.map(v => ({
      ...v,
      selected: !!advisor.accounts.find(a => a.id === v.id),
    }));

    openModal({
      type: 'AdvisorAccountsManagingModal',
      advisor: advisor,
      accountsList: preparedAccounts,
      onSuccess: () => {
        refreshAdvisors();
        refreshInstitutions();
      },
    });
  };

  useEffect(() => {
    if (!advisors || !institutions) {
      return;
    }

    const allInstitutionAccounts = institutions
      .map(v => v.accounts)
      .flat()
      .map(v => ({
        ...v,
        disabled: ['CUSTODIAN', 'MANAGED'].includes(v.sourceType),
      }));

    setAllAdvisors(advisors);
    setAllAccounts(allInstitutionAccounts);
  }, [advisors, institutions]);

  if (!advisors || !institutions) {
    return <LoadingSpinner/>;
  }

  if (advisorsError || institutionsError) {
    const error = advisorsError?.response?.data?.error || institutionsError?.response?.data?.error || {};

    return <DescriptiveText>{error.msg || 'Failed to load data from API.'}</DescriptiveText>;
  }

  return <Page>
    <StickyHeaderBar title={'ADVISORS'}/>
    <Column>
      {allAdvisors.map(advisor => (
        <Tile key={`Advisor-${advisor.id}`} title={`${advisor.firstName} ${advisor.lastName}`}>
          <Row margin={'10px 0'}
               wrap={true}
               justifySubRow={'left'}
               alignItems={'top'}
               showDividers={true}>
            <Column width={`${contactInfoColumnMinWidth}px`}
                    margin={'0 0 15px 0'}
                    alignItems={'left'}
                    justifySubColumn={'top'}
                    layout={{grow: 1}}>
              <DescriptiveText margin={'5px 5px 10px 10px'}
                               font={'ProximaSemibold'}
                               fontSize={'18px'}
                               alignment={'left'}
                               width={'fill'}>
                Contact Information
              </DescriptiveText>
              <Row margin={'5px 5px 0 5px'}>
                <TextInput type='phone'
                           title='Phone'
                           placeholder=''
                           dataPoint={{name: 'phone', value: advisor.phone}}
                           readOnly={true}/>
              </Row>
              <Row margin={'5px 5px 0 5px'}>
                <TextInput type='text'
                           title='Email'
                           placeholder=''
                           dataPoint={{name: 'email', value: advisor.email}}
                           readOnly={true}/>
              </Row>
            </Column>
            <Column minWidth={`${accountsManagingColumnMinWidth}px`}
                    width={`calc(100% - ${contactInfoColumnMinWidth + marginWidth}px)`}
                    alignItems={'left'}
                    layout={{grow: 1}}
                    justifySubColumn={'top'}>
              <Row wrap={false} width={'fit-content'}>
                <DescriptiveText margin={'5px 5px 10px 5px'}
                                 font={'ProximaSemibold'}
                                 fontSize={'18px'}
                                 alignment={'left'}>
                  Accounts Managing
                </DescriptiveText>
                <Icon svg={'circleEdit'}
                      height={'20px'}
                      width={'20px'}
                      margin={'0 0 5px 5px'}
                      action={() => openManageAdvisorAccountsModal(advisor)}
                      disabled={!!session.runAsInvestor}/>
              </Row>
              <Columnizer width='fill' justifySubRow='left' wrap={true}>
                {advisor.accounts.map((account) => (
                  <Column width={'260px'}
                          margin={'0 0 15px 0'}
                          key={`AdvisorAccount-${account.id}`}>
                    <DescriptiveText font={'ProximaRegular'}
                                     fontSize={'16px'}
                                     alignment={'left'}
                                     width={'fill'}>
                      {account.name}
                    </DescriptiveText>
                    <DescriptiveText margin={'5px 5px 10px 5px'}
                                     font={'ProximaLight'}
                                     fontSize={'14px'}
                                     alignment={'left'}
                                     width={'fill'}>
                      Managed Since: {formatter('date')(account.managedSince)}
                    </DescriptiveText>
                  </Column>
                ))}
              </Columnizer>
            </Column>
          </Row>
        </Tile>
      ))}
    </Column>
  </Page>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Advisors);

Advisors.propTypes = {
  /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
    runAsInvestor: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * The open function that dispatches _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
};
